import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import { locationApi } from '../services/location.js';
import { useAuth } from '../hooks/useAuth';
import Select from 'react-select';

const Login = () => {
  const [activeTab, setActiveTab] = useState('password');
  const [registrationTab, setRegistrationTab] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpCounter, setOtpCounter] = useState(0);
  
  // Registration form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regState, setRegState] = useState('');
  const [regCity, setRegCity] = useState('');
  const [regApplicantType, setRegApplicantType] = useState('Individual');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  
  const navigate = useNavigate();
  const { login, getCurrentUser } = useAuth();
  
  // Fetch states for registration form
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await locationApi.getStates();
        setStates(response.data.states || []);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    
    fetchStates();
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (regState) {
        try {
          const response = await locationApi.getCities(regState);
          setCities(response.data.cities || []);
        } catch (error) {
          console.error('Error fetching cities:', error);
        }
      } else {
        setCities([]);
      }
    };
    
    fetchCities();
  }, [regState]);
  
  // Set document title on mount and check auth status - with authentication check performed once
  useEffect(() => {
    document.title = 'Login - Wishfin Partner'; // Or just 'Login'
    
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const currentUser = JSON.parse(storedUser);
        console.log('User already logged in, redirecting to dashboard');
        
        // Redirect based on user type
        const userType = currentUser.user_type?.toUpperCase();
        switch (userType) {
          case 'DSO':
            navigate('/dso/home');
            break;
          case 'AGENT':
            navigate('/agent');
            break;
          case 'ADMIN':
            navigate('/admin');
            break;
          case 'RM':
            navigate('/rm/home');
            break;
          case 'BACKOFFICE':
            navigate('/backoffice/home');
            break;
          case 'WFBACKOFFICE':
            navigate('/wfbackoffice/dashboard');
            break;
          default:
            // Do nothing, stay on login page
            console.log('Unknown user type:', userType);
            break;
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }
  }, [navigate]); // Removed getCurrentUser as dependency
    
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Create FormData object to match FastAPI's OAuth2PasswordRequestForm requirement
      const formData = new FormData();
      formData.append('username', email); // FastAPI expects 'username' for email
      formData.append('password', password);
      
      console.log('Sending login request with:', {
        email: email,
        password: password ? '[REDACTED]' : 'empty'
      });
      
      // Call the login API with URLSearchParams (better for form data)
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('username', email);
      urlEncodedData.append('password', password);
      
      const response = await api.post('/auth/login', urlEncodedData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      // Extract data from response
      const { access_token, user } = response.data;
      
      // Store token and user in context and localStorage
      login(user, access_token);

      // Navigate based on user type with better logging
      console.log('Email login successful, navigating for user type:', user.user_type);
      
      const roleRouteMap = {
        'ADMIN': '/admin',
        'AGENT': '/agent',
        'DSO': '/dso',
        'RM': '/rm',
        'BACKOFFICE': '/backoffice',
        'WFBACKOFFICE': '/wfbackoffice'
      };
      
      const userType = user.user_type.toUpperCase();
      const redirectPath = roleRouteMap[userType];
      
      if (redirectPath) {
        console.log(`Navigating to ${redirectPath}`);
        navigate(redirectPath);
      } else {
        console.warn(`Unknown user type: ${user.user_type}`);
        navigate('/login');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Log the full error response for debugging
      console.log('Error response:', error.response);
      
      if (error.response) {
        console.log('Response status:', error.response.status);
        console.log('Response data:', error.response.data);
        
        // Handle error based on response
        if (error.response.data && error.response.data.detail) {
          setError(error.response.data.detail);
        } else if (error.response.status === 401) {
          setError('Invalid email or password');
        } else {
          setError('Authentication failed. Please check your credentials.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        setError('No response from server. Please try again later.');
      } else {
        // Something happened in setting up the request
        setError('Failed to login. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    
    console.log('Send OTP button clicked');
    console.log('Mobile number:', mobile);
    
    if (mobile.length !== 10 || !/^[6-9]\d{9}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      console.log('Invalid mobile number format');
      return;
    }
    
    setError('');
    setIsLoading(true);
    console.log('Sending OTP request to backend...');
    
    try {
      // Call the API to request OTP with only mobile number
      console.log('Making API call to /auth/request-otp with payload:', { mobile });
      const response = await api.post('/auth/request-otp', { mobile });
      console.log('OTP request successful, response:', response.data);
      
      setOtpSent(true);
      startOtpCounter();
    } catch (error) {
      console.error('OTP request error:', error);
      console.log('Error details:', error.response?.data || 'No response data');
      console.log('Error status:', error.response?.status || 'No status code');
      
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    const otpValue = otp.join('');
    if (otpValue.length !== 4 || !/^\d{4}$/.test(otpValue)) {
      setError('Please enter a valid 4-digit OTP');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      // Call the API to verify OTP
      const response = await api.post('/auth/verify-otp', { 
        mobile, 
        otp: otpValue 
      });
      
      // Extract data from response
      const { access_token, user } = response.data;
      
      // Store token and user in context and localStorage
      login(user, access_token);

      // Navigate based on user type with better logging
      console.log('OTP login successful, navigating for user type:', user.user_type);
      
      const roleRouteMap = {
        'ADMIN': '/admin',
        'AGENT': '/agent',
        'DSO': '/dso',
        'RM': '/rm',
        'BACKOFFICE': '/backoffice',
        'WFBACKOFFICE': '/wfbackoffice'
      };
      
      const userType = user.user_type.toUpperCase();
      const redirectPath = roleRouteMap[userType];
      
      if (redirectPath) {
        console.log(`Navigating to ${redirectPath}`);
        navigate(redirectPath);
      } else {
        console.warn(`Unknown user type: ${user.user_type}`);
        navigate('/login');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.response && error.response.status === 401) {
        setError('Invalid OTP. Please try again.');
      } else if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to verify OTP. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto focus next input if value is entered
      if (value && index < 3) {
        const nextInput = document.getElementById(`digit-${index + 2}`);
        if (nextInput) nextInput.focus();
      }
    }
  };
  
  const startOtpCounter = () => {
    setOtpCounter(30);
  };
  
  useEffect(() => {
    if (otpCounter > 0) {
      const timer = setTimeout(() => {
        setOtpCounter(otpCounter - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCounter]);

  // Simple function to allow only number input
  const numOnly = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className="leftImg">
        <img className="d-none d-lg-block" src="/static/images/loginLeftImg.png" alt="logo" />
      </div>

      <header className="py-3">
        <div className="container">
            <div className="logo text-end">
                <img src="/static/images/Wishfin-logo.png" alt="logo" />
            </div>
        </div>
      </header>
      
      <main>
        
        <div className="container">
          <div className="row justify-content-end">
            <div className="col-lg-10">
              <h2 className="mb-lg-4 mb-5 text-center text-lg-start">REGISTER / SIGN IN</h2>
              
              <div className="topnav mb-4">
                <button 
                  type="button" 
                  className={`withRegistration ${registrationTab ? 'active' : ''}`}
                  onClick={() => {setRegistrationTab(true);}}
                  style={{
                    backgroundColor: registrationTab ? '#28a745' : '#f8f8f8',
                    color: registrationTab ? 'white' : '#333',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    marginRight: '10px',
                    padding: '8px 15px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Register
                </button>
                <button 
                  type="button" 
                  className={`withPassword ${!registrationTab && activeTab === 'password' ? 'active' : ''}`}
                  onClick={() => {setActiveTab('password'); setRegistrationTab(false);}}
                >
                  Login with Password
                </button>
                <button 
                  type="button" 
                  className={`withOtp ${!registrationTab && activeTab === 'otp' ? 'active' : ''}`}
                  onClick={() => {setActiveTab('otp'); setRegistrationTab(false);}}
                >
                  Login with OTP
                </button>
              </div>
              
              <form 
                id="loginWithPassword" 
                className="pt-1" 
                onSubmit={handleEmailLogin}
                style={{ display: !registrationTab && activeTab === 'password' ? 'block' : 'none' }}
              >
                <div className="form-group">
                  <label htmlFor="email">Email ID</label>
                  <input 
                    name="email" 
                    type="email" 
                    id="email" 
                    maxLength="50"
                    minLength="2"
                    className="form-control required"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="Password">Password</label>
                  <input 
                    name="Password" 
                    type="password" 
                    id="Password" 
                    className="form-control required"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                </div>
                
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    name="RememberMe" 
                    defaultChecked 
                    role="switch"
                    id="RememberMe" 
                  />
                  <label className="form-check-label" htmlFor="RememberMe">Remember me</label>
                </div>
                
                <div className="formSubmitionError formSubmitionError1 text-center">
                  {error}
                </div>
                
                <div className="col-lg-12 my-4">
                  <div className="btns text-center">
                    <button 
                      className="submitBtn" 
                      type="submit"
                      style={{ display: isLoading ? 'none' : 'inline-block' }}
                    >
                      SIGN IN <img src="/static/images/arrow-right.png" alt="logo" />
                    </button>
                    <button 
                      type="button" 
                      className="spinnerBtn"
                      style={{ display: isLoading ? 'inline-block' : 'none' }}
                    >
                      <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                      Please Wait...
                    </button>
                  </div>
                </div>
              </form>
              
              <div 
                id="loginWithOtp" 
                className="pt-1"
                style={{ display: !registrationTab && activeTab === 'otp' ? 'block' : 'none' }}
              >
                <form 
                  id="sendOtpForm" 
                  className="mt-sm-5 mt-4" 
                  onSubmit={handleSendOtp}
                  style={{ display: otpSent ? 'none' : 'block' }}
                >
                  <div className="form-group position-relative mb-0">
                    <label htmlFor="otpMobile">Mobile Number</label>
                    <div className="mobileNumberWrapper">
                      <input 
                        autoComplete="off" 
                        type="tel" 
                        pattern="[6789][0-9]{9}" 
                        id="otpMobile" 
                        name="otpMobile" 
                        onKeyPress={numOnly}
                        placeholder="+91" 
                        className="lnt-input form-control" 
                        minLength="10" 
                        maxLength="10"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                        required 
                      />
                      <button 
                        className="sendOtpSubmitBtn" 
                        type="submit"
                        style={{ display: isLoading ? 'none' : 'inline-flex' }}
                      >
                        Send Otp <span className="phone-icon">📞</span>
                      </button>
                      <button 
                        type="button" 
                        className="sendOtpSpinnerBtn"
                        style={{ display: isLoading ? 'flex' : 'none' }}
                      >
                        <div className="spinner-border text-light" role="status"></div>
                      </button>
                    </div>
                  </div>
                  
                  <div className="formSubmitionError formSubmitionError2 text-center">
                    {error}
                  </div>
                </form>
                
                <div 
                  className="otpWrapper text-center"
                  style={{ display: otpSent ? 'block' : 'none' }}
                >
                  <form 
                    id="otp-verification-form" 
                    className="digit-group position-relative"
                    onSubmit={handleVerifyOtp}
                  >
                    <div className="form-group text-center inputWrapper mt-5 mb-4">
                      <input
                        type="tel"
                        className="digit __mr_20 text-center"
                        id="digit-1"
                        name="digit-1"
                        data-next="digit-2"
                        onKeyPress={numOnly}
                        maxLength="1"
                        value={otp[0]}
                        onChange={(e) => handleOtpChange(0, e.target.value)}
                        required
                      />
                      <input
                        type="tel"
                        className="digit __mr_20 text-center"
                        id="digit-2"
                        name="digit-2"
                        data-next="digit-3"
                        data-previous="digit-1"
                        onKeyPress={numOnly}
                        maxLength="1"
                        value={otp[1]}
                        onChange={(e) => handleOtpChange(1, e.target.value)}
                        required
                      />
                      <input
                        type="tel"
                        className="digit __mr_20 text-center"
                        id="digit-3"
                        name="digit-3"
                        data-next="digit-4"
                        data-previous="digit-2"
                        onKeyPress={numOnly}
                        maxLength="1"
                        value={otp[2]}
                        onChange={(e) => handleOtpChange(2, e.target.value)}
                        required
                      />
                      <input
                        type="tel"
                        className="digit text-center"
                        id="digit-4"
                        name="digit-4"
                        data-previous="digit-3"
                        onKeyPress={numOnly}
                        maxLength="1"
                        value={otp[3]}
                        onChange={(e) => handleOtpChange(3, e.target.value)}
                        required
                      />
                      <input id="otp" type="hidden" name="otp" minLength="4" maxLength="4" />
                      <input type="hidden" name="otp_mobile_number" id="otp_mobile_number" value={mobile} />
                      <p className="help-block otp_error_box mt-3"></p>
                      <p className="success-message mt-3" style={{ color: 'green', fontSize: '14px' }}></p>
                    </div>
                    
                    <div className="formSubmitionError formSubmitionError3 text-center">
                      {error}
                    </div>
                    
                    <div className="btnFroup mt-sm-5 mt-4">
                      <p className="mb-3 resend-btn text-center">
                        {otpCounter > 0 ? (
                          <span id="otp_counter">Resend OTP in {otpCounter} seconds</span>
                        ) : (
                          <span 
                            id="resend_otp" 
                            role="button" 
                            onClick={() => handleSendOtp()}
                          >
                            <strong className="__theme_color">Resend</strong>
                          </span>
                        )}
                      </p>
                      
                      <div className="text-center">
                        <button 
                          className="otpSubmitBtn" 
                          type="submit"
                          style={{ display: isLoading ? 'none' : 'inline-block' }}
                        >
                          Verify
                        </button>
                        <button 
                          type="button" 
                          className="otpSpinnerBtn"
                          style={{ display: isLoading ? 'inline-flex' : 'none' }}
                        >
                          <div className="spinner-border text-light" role="status"></div>
                          Please Wait...
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Registration Form */}
              <form
                id="dsoRegistrationForm"
                className="pt-1"
                style={{ display: registrationTab ? 'block' : 'none' }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  setRegError('');
                  setRegLoading(true);
                  
                  // Validate form
                  if (regPassword !== regConfirmPassword) {
                    setRegError('Passwords do not match');
                    setRegLoading(false);
                    return;
                  }
                  
                  if (!/^[6-9]\d{9}$/.test(regPhone)) {
                    setRegError('Please enter a valid 10-digit mobile number');
                    setRegLoading(false);
                    return;
                  }
                  
                  try {
                    // Construct the DSA registration data
                    const dsoData = {
                      name: regName,
                      email: regEmail,
                      mobile: regPhone,
                      password: regPassword,
                      user_type: 'DSO', // Backend type still remains DSO
                      state_id: regState ? parseInt(regState, 10) : null,
                      city_id: regCity ? parseInt(regCity, 10) : null,
                      applicant_type: regApplicantType // Individual or Business
                    };
                    
                    // Call the registration API
                    const registrationResponse = await api.post('/auth/register-dso', dsoData);
                    console.log('Registration successful:', registrationResponse.data);
                    
                    // Now automatically log in the user with the newly created credentials
                    try {
                      // Create FormData object to match FastAPI's OAuth2PasswordRequestForm requirement
                      const urlEncodedData = new URLSearchParams();
                      urlEncodedData.append('username', regEmail);
                      urlEncodedData.append('password', regPassword);
                      
                      const loginResponse = await api.post('/auth/login', urlEncodedData, {
                        headers: {
                          'Content-Type': 'application/x-www-form-urlencoded'
                        }
                      });
                      
                      // Extract data from login response
                      const { access_token, user } = loginResponse.data;
                      
                      // Store token and user in context and localStorage
                      login(user, access_token);
                      
                      // Navigate to DSO onboarding page
                      navigate('/dso/onboarding');
                      
                    } catch (loginError) {
                      console.error('Auto login failed:', loginError);
                      
                      // If auto-login fails, show success message and reset form
                      setRegSuccess(true);
                      setRegName('');
                      setRegEmail('');
                      setRegPhone('');
                      setRegPassword('');
                      setRegConfirmPassword('');
                      setRegState('');
                      setRegCity('');
                      setRegApplicantType('Individual');
                      
                      // After 5 seconds, switch to login tab
                      setTimeout(() => {
                        setRegistrationTab(false);
                        setActiveTab('password');
                        setRegSuccess(false);
                      }, 5000);
                    }
                    
                  } catch (error) {
                    console.error('Registration error:', error);
                    if (error.response && error.response.data && error.response.data.detail) {
                      setRegError(error.response.data.detail);
                    } else {
                      setRegError('Registration failed. Please try again.');
                    }
                  } finally {
                    setRegLoading(false);
                  }
                }}
              >
                {regSuccess ? (
                  <div className="alert alert-success text-center py-4 mb-4">
                    <div className="mb-3" style={{ fontSize: '3rem' }}>✓</div>
                    <h4 className="alert-heading mb-3">Registration Successful!</h4>
                    <p className="mb-2">Your DSA account has been created successfully.</p>
                    <p>You will be redirected to the login page shortly...</p>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>DSA Type</label>
                      <div className="registration-type-selector mb-3" style={{ display: 'flex', border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden' }}>
                        <div 
                          className={regApplicantType === 'Individual' ? 'selected' : ''} 
                          style={{ 
                            flex: 1, 
                            padding: '10px', 
                            textAlign: 'center', 
                            cursor: 'pointer',
                            backgroundColor: regApplicantType === 'Individual' ? '#0d6efd' : '#f8f9fa',
                            color: regApplicantType === 'Individual' ? '#fff' : '#212529',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => setRegApplicantType('Individual')}
                        >
                          Individual
                        </div>
                        <div 
                          className={regApplicantType === 'Business' ? 'selected' : ''} 
                          style={{ 
                            flex: 1, 
                            padding: '10px', 
                            textAlign: 'center', 
                            cursor: 'pointer',
                            backgroundColor: regApplicantType === 'Business' ? '#0d6efd' : '#f8f9fa',
                            color: regApplicantType === 'Business' ? '#fff' : '#212529',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => setRegApplicantType('Business')}
                        >
                          Business
                        </div>
                      </div>
                      <div className="text-muted small" style={{ marginTop: '-5px', marginBottom: '15px' }}>
                        {regApplicantType === 'Individual' 
                          ? 'For individual Direct Selling Agents' 
                          : 'For companies operating as Direct Selling Agents'}
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="regName" className="mb-1">{regApplicantType === 'Individual' ? 'Full Name' : 'Business Name'}</label>
                      <input
                        name="regName"
                        type="text"
                        id="regName"
                        className="form-control required"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="regEmail" className="mb-1">Email ID</label>
                      <input
                        name="regEmail"
                        type="email"
                        id="regEmail"
                        className="form-control required"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="regPhone" className="mb-1">Phone Number</label>
                      <input
                        name="regPhone"
                        type="tel"
                        id="regPhone"
                        pattern="[6-9][0-9]{9}"
                        maxLength="10"
                        className="form-control required"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, ''))}
                        required
                      />
                      <div className="text-muted small">Enter a 10-digit mobile number starting with 6-9</div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="regState" className="mb-1">State</label>
                      <Select
                        id="regState"
                        name="regState"
                        options={states.map(state => ({ value: state.id, label: state.name }))}
                        value={regState ? { value: regState, label: states.find(s => s.id === parseInt(regState))?.name || '' } : null}
                        onChange={(selectedOption) => setRegState(selectedOption ? selectedOption.value : '')}
                        placeholder="Select State"
                        isClearable
                        isSearchable
                        required
                        className="react-select-container mb-3"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#86b7fe' : '#ced4da'
                            },
                            minHeight: '38px'
                          })
                        }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="regCity" className="mb-1">City</label>
                      <Select
                        id="regCity"
                        name="regCity"
                        options={cities.map(city => ({ value: city.id, label: city.name }))}
                        value={regCity ? { value: regCity, label: cities.find(c => c.id === parseInt(regCity))?.name || '' } : null}
                        onChange={(selectedOption) => setRegCity(selectedOption ? selectedOption.value : '')}
                        placeholder="Select City"
                        isClearable
                        isSearchable
                        isDisabled={!regState}
                        required
                        className="react-select-container mb-3"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#86b7fe' : '#ced4da'
                            },
                            backgroundColor: !regState ? '#e9ecef' : 'white',
                            minHeight: '38px'
                          })
                        }}
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="regPassword" className="mb-1">Password</label>
                      <input
                        name="regPassword"
                        type="password"
                        id="regPassword"
                        className="form-control required"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        minLength="8"
                        required
                      />
                      <div className="text-muted small">Minimum 8 characters</div>
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="regConfirmPassword" className="mb-1">Confirm Password</label>
                      <input
                        name="regConfirmPassword"
                        type="password"
                        id="regConfirmPassword"
                        className="form-control required"
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        minLength="8"
                        required
                      />
                    </div>
                    
                    {regError && (
                      <div className="alert alert-danger text-center mb-3">
                        {regError}
                      </div>
                    )}
                    
                    <div className="my-4 pt-2">
                      <div className="d-grid gap-2">
                        <button
                          className="submitBtn btn btn-primary btn-lg"
                          type="submit"
                          style={{ display: regLoading ? 'none' : 'block' }}
                        >
                          <span className="d-flex align-items-center justify-content-center">
                            REGISTER <img src="/static/images/arrow-right.png" alt="logo" className="ms-2" />
                          </span>
                        </button>
                        <button
                          type="button"
                          className="spinnerBtn btn btn-primary btn-lg"
                          style={{ display: regLoading ? 'block' : 'none' }}
                        >
                          <span className="d-flex align-items-center justify-content-center">
                            <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                            Please Wait...
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
            <div className="col-lg-1"></div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Login;
